// Palworld OFFICIAL CARD GAME promo (PR) card guide + FAQ.
// Sourced from the official English card list (PR section) and secondary
// market indexes (CardNexus Promotional Cards 2026 / PR2026), cross-checked
// against community listings on eBay, Discord and social channels.
import type { Guide, ExternalLink } from "./guides-data";

const L: Record<string, ExternalLink> = {
  official: {
    label: "Palworld OFFICIAL CARD GAME — Card List",
    url: "https://en.palworld-official-cardgame.com/cardlist",
    note: "Official English card list including the PR section.",
  },
  news: {
    label: "Palworld OFFICIAL CARD GAME — News",
    url: "https://en.palworld-official-cardgame.com/news/",
    note: "Release-event and promo distribution announcements.",
  },
  events: {
    label: "Palworld OFFICIAL CARD GAME — Events",
    url: "https://en.palworld-official-cardgame.com/events/",
    note: "Shop tournament and Demo Caravan schedule.",
  },
  cardnexus: {
    label: "CardNexus — Promotional Cards 2026 (PR2026)",
    url: "https://cardnexus.com/en/explore/palworld/promotional-cards-2026/all",
    note: "Secondary-market index of every printed PR card.",
  },
  products: {
    label: "Palworld OFFICIAL CARD GAME — Products",
    url: "https://en.palworld-official-cardgame.com/products/",
    note: "Product bundles that ship promo inserts.",
  },
};

const pool = (a: ExternalLink, b: ExternalLink, c: ExternalLink): ExternalLink[] => [a, b, c];

export const PROMO_GUIDES: Guide[] = [
  {
    slug: "palworld-tcg-pr-promo-cards",
    title: "Palworld TCG PR Promo Cards — Every Promotional Card (2026)",
    desc: "Every Palworld TCG promo card printed so far: EPR-001 Lamball My First Pal, ESOUL-000 and ESOUL-003 Soul promos, how to get them, value and a promo card FAQ.",
    tag: "TCG Cards",
    updated: "2026-08-04",
    keywords:
      "palworld tcg promo cards, palworld pr cards, EPR-001 lamball my first pal, ESOUL-000, ESOUL-003, palworld promotional cards 2026, PR2026 palworld, palworld promo card value, how to get palworld promo cards, palworld shop tournament promo",
    articles: [
      {
        title: "Every Palworld PR promo card released so far",
        slug: "every-palworld-pr-promo-card",
        intro:
          "The Palworld OFFICIAL CARD GAME PR line — catalogued as Promotional Cards 2026 (PR2026) — currently contains three printed cards: EPR-001 Lamball “My First Pal”, ESOUL-000 and ESOUL-003. None of them are pulled from booster packs; every one is handed out through an event, a demo session or a product bundle.",
        sections: [
          {
            heading: "EPR-001 Lamball — My First Pal",
            text: "A Colorless ◇2 Pal card with 200 power and 1 strike, tagged Crafting, Transporting and Farming. Its CONT ability stops it being attacked by ◇4 or greater Pals, which makes it a genuinely playable early blocker rather than a pure collectible. It is the flagship beginner promo handed out at demo sessions and starter events.",
          },
          {
            heading: "ESOUL-000 Soul (Cattiva art)",
            text: "A promo Soul card with no cost, power or strike. Soul cards sit outside the deck as a starting resource, so ESOUL-000 is a cosmetic upgrade over the Soul you get in a Trial Deck — same function, alternate Cattiva illustration.",
          },
          {
            heading: "ESOUL-003 Soul",
            text: "The second promo Soul printing. Like ESOUL-000 it is a functional replacement for the standard Soul card, distributed alongside events and product bundles rather than in boosters.",
          },
          {
            heading: "How PR numbering works",
            text: "Promos use the EPR- prefix for Pal and support cards and ESOUL- for Soul cards, with a three-digit collector number and no rarity suffix. All of them carry the PR rarity code on Pal Stars, and all three are indexed in the Card Database under the PR set filter.",
          },
        ],
        externalLinks: pool(L.official, L.cardnexus, L.products),
      },
      {
        title: "How to get Palworld promo cards in 2026",
        slug: "how-to-get-palworld-promo-cards",
        intro:
          "PR cards are distribution rewards, not pack pulls. There are four realistic routes, and three of them are free.",
        sections: [
          {
            heading: "Demo sessions and the Demo Caravan",
            text: "Official stores run demo sessions with all materials provided, and the Demo Caravan tours 200+ stores worldwide. Players who finish a demo game normally leave with a promo — this is how most EPR-001 Lamball copies reached collectors.",
          },
          {
            heading: "Shop tournaments",
            text: "Bushiroad's grassroots tier runs at official tournament stores, and a new promo enters the distribution cycle roughly every three months. Entry is paid at the store, and participation — not winning — is usually enough to receive the current promo.",
          },
          {
            heading: "Product bundles and release events",
            text: "Promo Soul cards ship as inserts with select product bundles and release-weekend events. Check the official Products and News pages before pre-ordering; the insert is listed on the product page when one is included.",
          },
          {
            heading: "Secondary market",
            text: "eBay, TCG marketplaces and Discord trade servers carry all three PR cards. Because print runs are event-limited rather than set-limited, supply is thin and listings frequently show as out of stock on price indexes.",
          },
        ],
        externalLinks: pool(L.events, L.news, L.cardnexus),
      },
      {
        title: "Are Palworld promo cards tournament legal?",
        slug: "are-palworld-promo-cards-tournament-legal",
        intro:
          "Yes — PR promos are fully legal in sanctioned Palworld OFFICIAL CARD GAME play, with the same copy limits as any other printing.",
        sections: [
          {
            heading: "Legality and copy limits",
            text: "A promo counts as a printing of the card it names. EPR-001 Lamball counts toward the standard four-copies-per-card deck limit alongside any booster printing of Lamball, so you cannot stack promos to exceed the limit.",
          },
          {
            heading: "Soul promos in play",
            text: "ESOUL-000 and ESOUL-003 replace the Soul card you already start with. Swapping in a promo Soul changes nothing mechanically, which is exactly why judges allow them without restriction.",
          },
          {
            heading: "Sleeving and condition rules",
            text: "Promos are usually handed out loose and pick up edge wear fast. Sleeve them immediately — a marked or distinguishable card can be ruled unplayable even when the printing itself is legal.",
          },
          {
            heading: "Language mixing",
            text: "English PR printings are legal in English-language events. Japanese promos are legal in Japanese events; check the event page before travelling with a mixed-language deck.",
          },
        ],
        externalLinks: pool(L.official, L.events, L.news),
      },
      {
        title: "Palworld promo card value and collecting strategy",
        slug: "palworld-promo-card-value",
        intro:
          "Promo pricing behaves differently from booster chase cards: supply is capped by attendance, not by pull rate, so early promos drift upward long after the set stops printing.",
        sections: [
          {
            heading: "Why PR cards hold value",
            text: "EPR-001 and the ESOUL promos were never in a booster print run. Once the distribution window closes there is no reprint source, so the float only shrinks as copies get played, damaged or graded.",
          },
          {
            heading: "Grading is worth it here",
            text: "Because promos are handed out loose, high-grade copies are scarce relative to the total print run. A gem-mint promo typically commands a much larger premium over raw than a gem-mint booster card of the same rarity.",
          },
          {
            heading: "What to buy first",
            text: "Prioritise EPR-001 Lamball — it is a playable card, the flagship beginner promo and the most recognisable PR2026 printing. Soul promos are cosmetic, so treat them as completionist pickups rather than investments.",
          },
          {
            heading: "Avoiding fakes",
            text: "Compare the printed code, art crop and color band against the official card list before buying. Verify any code you are offered with the Pal Stars Card Checker at /card-checker, and be sceptical of promos offered in bulk quantities.",
          },
        ],
        externalLinks: pool(L.cardnexus, L.official, L.products),
      },
      {
        title: "Palworld TCG promo card FAQ",
        slug: "palworld-tcg-promo-card-faq",
        intro:
          "Fast answers to the most-asked questions about Palworld PR promo cards, their card numbers and how to get them.",
        sections: [
          {
            heading: "How many Palworld promo cards are there?",
            text: "Three PR cards are printed and catalogued so far: EPR-001 Lamball “My First Pal”, ESOUL-000 Soul (Cattiva art) and ESOUL-003 Soul. All three are listed in the Pal Stars Card Database under the PR set filter.",
          },
          {
            heading: "Can you pull promo cards from Palworld booster packs?",
            text: "No. PR cards are never inserted into Dawn of Palpagos or any other booster pack — they only come from demo sessions, shop tournaments, release events and select product bundles.",
          },
          {
            heading: "What is EPR-001 Lamball My First Pal?",
            text: "EPR-001 is a Colorless ◇2 promo Pal card with 200 power and 1 strike whose CONT ability prevents it from being attacked by ◇4 or greater Pals, tagged for Crafting, Transporting and Farming.",
          },
          {
            heading: "Are ESOUL-000 and ESOUL-003 different from a normal Soul card?",
            text: "No, they function identically to the Soul card included in a Trial Deck. The difference is illustration and scarcity, not rules text.",
          },
          {
            heading: "How often do new Palworld promo cards come out?",
            text: "Official stores enter a new promo into the Shop Tournament distribution cycle roughly every three months, with extra promos attached to product releases and demo tours.",
          },
        ],
        externalLinks: pool(L.official, L.events, L.cardnexus),
      },
    ],
  },
];
